import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { RectangleVertical, Square, RectangleHorizontal, Smartphone } from 'lucide-react';
import type { ImageUseCase } from './ImageUseCaseSelector';

export interface ImageConfig {
  aspectRatio: string;
}

interface ImageConfigFormProps {
  config: ImageConfig;
  onChange: (config: ImageConfig) => void;
  useCase: ImageUseCase;
}

/**
 * ImageConfigForm - Aspect ratio selection for AI image generation
 *
 * Aspect ratios vary by use case:
 *
 * Static Image:
 * - 1:1: Square (emails, social media)
 * - 4:5: Vertical feed post (RECOMMENDED for social media)
 * - 16:9: Landscape (emails, social media)
 *
 * Video Thumbnail:
 * - 9:16: Portrait (auto-selected, only option)
 */
export const ImageConfigForm: React.FC<ImageConfigFormProps> = ({
  config,
  onChange,
  useCase
}) => {
  const staticImageRatios = [
    {
      value: '4:5',
      label: 'Vertical',
      ratio: '4:5',
      description: 'Create a vertical feed post for social media',
      recommended: true,
      icon: RectangleVertical
    },
    {
      value: '1:1',
      label: 'Square',
      ratio: '1:1',
      description: 'Create a square image for emails or social media',
      recommended: false,
      icon: Square
    },
    {
      value: '16:9',
      label: 'Landscape',
      ratio: '16:9',
      description: 'Create a landscape image for emails or social media',
      recommended: false,
      icon: RectangleHorizontal
    }
  ];

  const videoThumbnailRatios = [
    {
      value: '9:16',
      label: 'Portrait',
      ratio: '9:16',
      description: 'Vertical format for video thumbnails',
      recommended: false,
      icon: Smartphone
    }
  ];

  const aspectRatios = useCase === 'static-image' ? staticImageRatios : videoThumbnailRatios;

  return (
    <div className="space-y-2">
      <Label>Aspect Ratio</Label>
      <RadioGroup
        value={config.aspectRatio}
        onValueChange={(value) => onChange({ ...config, aspectRatio: value })}
        className={`grid gap-3 ${
          useCase === 'static-image' ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1'
        }`}
      >
        {aspectRatios.map((ratio) => (
          <HoverCard key={ratio.value} openDelay={200}>
            <HoverCardTrigger asChild>
              <div>
                <RadioGroupItem
                  value={ratio.value}
                  id={`ratio-${ratio.value}`}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={`ratio-${ratio.value}`}
                  className="flex flex-col items-center justify-center space-y-2 rounded-lg border-2 border-muted p-4 cursor-pointer hover:border-muted-foreground/40 transition-all peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/10 h-full min-h-[100px]"
                >
                  {/* Icon */}
                  <ratio.icon className="h-10 w-10 text-muted-foreground" />

                  {/* Label and Ratio - inline */}
                  <div className="flex flex-col items-center gap-1">
                    <span className="font-semibold text-sm">
                      {ratio.label} <span className="text-muted-foreground font-normal">({ratio.ratio})</span>
                    </span>
                    {ratio.recommended && (
                      <Badge variant="secondary" className="text-xs">
                        Recommended
                      </Badge>
                    )}
                  </div>
                </Label>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">{ratio.label} ({ratio.ratio})</h4>
                <p className="text-sm text-muted-foreground">{ratio.description}</p>
                {ratio.recommended && (
                  <p className="text-xs text-primary font-medium">
                    ✨ Recommended for best social media engagement
                  </p>
                )}
              </div>
            </HoverCardContent>
          </HoverCard>
        ))}
      </RadioGroup>
      <p className="text-xs text-muted-foreground">
        {useCase === 'video-thumbnail'
          ? 'Video thumbnails use a fixed 9:16 portrait format'
          : 'Hover over each option to learn more'}
      </p>
    </div>
  );
};

export default ImageConfigForm;
